import React, { useState, useRef, useEffect } from 'react';
import { Camera, CameraRotate, X, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CameraCapture = ({ onPhotoTaken, onClose }) => {
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [isUploading, setIsUploading] = useState(false);
  const [caption, setCaption] = useState('');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Start camera automatically when component mounts
  useEffect(() => {
    startCamera();
    
    // Cleanup: stop camera when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async (mode = facingMode) => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('❌ Your browser does not support camera. Please use Chrome, Safari, or Firefox.');
        return;
      }

      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      console.log('📷 Requesting camera permission...');
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode }
      });
      console.log('✅ Camera permission granted');

      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (error) {
      console.error('❌ Camera access denied:', error);
      
      let errorMessage = '❌ Camera access denied.\n\n';
      
      if (error.name === 'NotAllowedError') {
        errorMessage += '📱 Please allow camera permission in your browser settings.\n\n';
        errorMessage += 'Settings → Site Permissions → Camera → Allow';
      } else if (error.name === 'NotFoundError') {
        errorMessage += '📷 No camera found. Please check if your device has a camera.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += '📷 Camera is being used by another app. Please close other apps and try again.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      
      alert(errorMessage);
      onClose();
    }
  };

  const switchCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    startCamera(newMode);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setCapturedImage({ url, blob });
        
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const retake = () => {
    setCapturedImage(null);
    startCamera();
  };

  const sendPhoto = async () => {
    if (!capturedImage) return;
    onPhotoTaken({
      type: 'image',
      url: capturedImage.url,
      blob: capturedImage.blob,
      caption: caption || null
    });
  };

  React.useEffect(() => {
    startCamera();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      <div className="flex items-center justify-between p-4 text-white">
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
          <X size={28} weight="bold" />
        </button>
        <h3 className="text-lg font-semibold">📷 Camera</h3>
        <div className="w-10" />
      </div>

      <div className="flex-1 relative flex items-center justify-center">
        {!capturedImage ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
          </>
        ) : (
          <img
            src={capturedImage.url}
            alt="Captured"
            className="w-full h-full object-contain"
          />
        )}
      </div>

      <div className="p-6 bg-black/50 flex flex-col items-center gap-3">
        {capturedImage && (
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add caption..."
            className="w-full max-w-md px-4 py-2 bg-white/10 text-white rounded-full border border-white/20 placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="camera-caption-input"
          />
        )}
        <div className="flex items-center justify-center gap-6">
        {!capturedImage ? (
          <>
            <button
              onClick={switchCamera}
              className="p-4 bg-white/20 text-white rounded-full hover:bg-white/30"
            >
              <CameraRotate size={28} />
            </button>
            <button
              onClick={capturePhoto}
              className="p-6 bg-white rounded-full hover:bg-gray-200"
              data-testid="capture-photo-btn"
            >
              <Camera size={32} />
            </button>
            <div className="w-16" />
          </>
        ) : (
          <>
            <button
              onClick={retake}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Retake
            </button>
            <button
              onClick={sendPhoto}
              disabled={isUploading}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
              data-testid="camera-send-btn"
            >
              <Check size={20} weight="bold" />
              {isUploading ? 'Sending...' : 'Send'}
            </button>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
